import { forwardRef, useCallback } from "react";
import { Box, Button, Stack, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { Text } from "@imminently/react/ui/core/text";

import ObservationTable from "@/components/ObservationChart/ObservationTable";
import ObservationChart from "@/components/ObservationChart/ObservationChart";
import { useObsChartCtx } from "@/components/ObservationChart/ObservationChart_ctx";
import { TabSubSection } from "@/components/TabSection";
import { useZoomContentModal } from "@/modals/zoomedContent";
import { Observation, Patient as PatientType } from "@/resources/types";
import { formatName } from "@/global";
import { Icon } from "@/components/Icon";
import { TableSections } from "@/components/ObservationChart";
import { ObsTabs, useObsTabsCtx } from "@/components/ObservationChart/ObservationTabs_ctx";

enum ObservationTabNames {
  table = "table",
  chart = "chart"
}

export type ObservationTableSubSectionProps = {
  observations: Observation[] | undefined;
  patient: PatientType;
  tableSections: TableSections;
  encounterId: string;
  onEditObservation?: (observation: Observation) => void;
};

export const tabsWithOnlyTable = [
  ObsTabs.NEUROLOGICAL,
  ObsTabs.URINALYSIS,
];

export const PatientObservationChart = forwardRef<HTMLTableElement, ObservationTableSubSectionProps>(({ observations, patient, tableSections, encounterId, onEditObservation }, ref) => {
  const obsChartCtxValue = useObsChartCtx();
  const { value: { tab } } = useObsTabsCtx();

  const observationTable = (
    <ObservationTable
      ref={ref}
      observations={observations || []}
      tableSections={tableSections}
      type={tab}
      encounterId={encounterId}
      onEditObservation={onEditObservation}
    />
  );

  const openZoomedObservationSummary = useZoomContentModal();

  const openZoomed = useCallback(() => {
    openZoomedObservationSummary({
      component: observationTable,
      title: formatName(patient.firstName, patient.lastName),
    });
  }, [observations, tableSections, ref]);

  const showChart = obsChartCtxValue.value.isActive;

  const isShownToggle = !tabsWithOnlyTable.includes(tab);

  return (
    <TabSubSection>
      <Text italic={true} variant="body1" sx={{ padding: "0px 0px 20px 0px" }}>This observation chart, warnings and risk score are not designed for Obstetric patients, or patients younger than 16 years of age. Please seek expert advice.</Text>
      <Stack direction="row" justifyContent={isShownToggle ? "space-between" : "end"} alignItems="center" pb={2}>

        {isShownToggle && <ToggleButtonGroup value={showChart ? ObservationTabNames.chart : ObservationTabNames.table}>
          <ToggleButton
            sx={{ gap: 1 }}
            value={ObservationTabNames.table}
            onClick={() => obsChartCtxValue.set({ isActive: false })}
          >
            <Text noWrap>Table</Text>
          </ToggleButton>
          <ToggleButton
            sx={{ gap: 1 }}
            value={ObservationTabNames.chart}
            onClick={() => obsChartCtxValue.set({ isActive: true })}
          >
            <Text noWrap>Chart</Text>
          </ToggleButton>
        </ToggleButtonGroup>}
        {!showChart && <Button onClick={openZoomed} startIcon={<Icon icon="enlarge" />}>Expand</Button>}
      </Stack>

      {showChart ? (
        <Box height={500} position='relative' mt={2}>
          <ObservationChart observations={observations || []} />
        </Box>
      ) : observationTable}
    </TabSubSection>
  );
});

PatientObservationChart.displayName = "PatientObservationChart";
